import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Image as ImageIcon, 
  Check, 
  X, 
  Clock, 
  Filter, 
  Download, 
  ExternalLink,
  ChevronRight,
  Loader2,
  Maximize2,
  BarChart3,
  Layers,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import supabase from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MyRendersPage
 * =============
 * Render history with timeline, status, quality metrics, and comparison.
 * Dark luxury UI consistent with SlabReality design.
 * 
 * 100% Supabase - NO MongoDB
 */
export default function MyRendersPage() {
  const { user } = useAuth();
  const [renders, setRenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRender, setSelectedRender] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchRenders();
    }
  }, [user?.id, statusFilter]);

  const fetchRenders = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('sre_render_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query.limit(50);
      
      if (error) {
        // Table might not exist yet
        if (error.code === '42P01') {
          console.log('sre_render_history table not found');
          setRenders([]);
          return;
        }
        throw error;
      }
      
      setRenders(data || []);
    } catch (err) {
      console.error('Error fetching renders:', err);
      toast.error('Errore nel caricamento dei render');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (renderId, newStatus) => {
    try {
      const { error } = await supabase
        .from('sre_render_history')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', renderId);
      
      if (error) throw error;
      
      // Update local state
      setRenders(prev => prev.map(r => 
        r.id === renderId ? { ...r, status: newStatus } : r
      ));
      
      toast.success(`Render ${newStatus === 'accepted' ? 'approvato' : 'rifiutato'}`);
    } catch (err) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const downloadRender = async (render) => {
    try {
      const response = await fetch(render.result_image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `slabreality_render_${render.id.slice(0, 8)}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore nel download');
    }
  };

  const statusCounts = {
    all: renders.length,
    draft: renders.filter(r => r.status === 'draft').length,
    accepted: renders.filter(r => r.status === 'accepted').length,
    rejected: renders.filter(r => r.status === 'rejected').length
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-zinc-800/50 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-light tracking-wide">I Miei Render</h1>
              <p className="text-zinc-500 mt-1">Storico delle sostituzioni superficie</p>
            </div>
            <Link
              to="/sre-v2"
              className="px-6 py-3 bg-gradient-to-r from-[#B08B5C] to-[#C8A36A] text-black font-medium rounded-lg hover:brightness-110 transition-all flex items-center gap-2"
            >
              <Layers className="w-5 h-5" />
              Nuovo Render
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { key: 'all', label: 'Tutti', icon: Layers },
            { key: 'draft', label: 'In attesa', icon: Clock },
            { key: 'accepted', label: 'Approvati', icon: Check },
            { key: 'rejected', label: 'Rifiutati', icon: X }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                  statusFilter === tab.key
                    ? 'bg-[#B08B5C] text-black'
                    : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                <span className="text-xs opacity-60">({statusCounts[tab.key]})</span>
              </button>
            );
          })}
        </div>

        {/* Renders Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#B08B5C] animate-spin" />
          </div>
        ) : renders.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-400 mb-2">Nessun render</h3>
            <p className="text-zinc-600 mb-6">Crea il tuo primo render con SlabReality Engine</p>
            <Link
              to="/sre-v2"
              className="px-6 py-3 bg-[#B08B5C] text-black font-medium rounded-lg hover:brightness-110 transition-all inline-flex items-center gap-2"
            >
              <Layers className="w-5 h-5" />
              Inizia
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {renders.map((render) => (
              <RenderCard
                key={render.id}
                render={render}
                onStatusUpdate={handleStatusUpdate}
                onDownload={() => downloadRender(render)}
                onCompare={() => {
                  setSelectedRender(render);
                  setShowComparison(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Comparison Modal */}
      {showComparison && selectedRender && (
        <ComparisonModal
          render={selectedRender}
          onClose={() => {
            setShowComparison(false);
            setSelectedRender(null);
          }}
        />
      )}
    </div>
  );
}

// Render Card Component
function RenderCard({ render, onStatusUpdate, onDownload, onCompare }) {
  const metrics = render.quality_metrics || {};
  
  const statusConfig = {
    draft: { label: 'In attesa', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
    accepted: { label: 'Approvato', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: Check },
    rejected: { label: 'Rifiutato', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: X }
  };
  
  const status = statusConfig[render.status] || statusConfig.draft;
  const StatusIcon = status.icon;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden group hover:border-zinc-700 transition-all">
      {/* Image */}
      <div className="relative aspect-[4/3] bg-zinc-800">
        <img
          src={render.result_image_url}
          alt="Render"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        
        {/* Status Badge */}
        <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium border ${status.color}`}>
          <StatusIcon className="w-3 h-3 inline mr-1" />
          {status.label}
        </div>
        
        {/* Hover Actions */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
          <button
            onClick={onCompare}
            className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            title="Confronta"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
          <button
            onClick={onDownload}
            className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            title="Download"
          >
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-medium text-white truncate mb-1">
          {render.material_name || 'Render senza titolo'}
        </h3>
        <p className="text-sm text-zinc-500 mb-3">
          {new Date(render.created_at).toLocaleDateString('it-IT', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
        
        {/* Quality Metrics */}
        {metrics.realism_score && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-zinc-500">Realismo</span>
              <span className="text-[#C8A36A] font-medium">{metrics.realism_score}%</span>
            </div>
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#B08B5C] to-[#C8A36A]"
                style={{ width: `${metrics.realism_score}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Mini Metrics Grid */}
        {(metrics.lighting_score || metrics.edge_quality_score) && (
          <div className="grid grid-cols-2 gap-2 text-xs mb-4">
            {metrics.lighting_score && (
              <div className="bg-zinc-800/50 rounded px-2 py-1">
                <span className="text-zinc-500">Luce</span>
                <span className="float-right text-zinc-300">{metrics.lighting_score}%</span>
              </div>
            )}
            {metrics.edge_quality_score && (
              <div className="bg-zinc-800/50 rounded px-2 py-1">
                <span className="text-zinc-500">Bordi</span>
                <span className="float-right text-zinc-300">{metrics.edge_quality_score}%</span>
              </div>
            )}
          </div>
        )}
        
        {/* Actions */}
        {render.status === 'draft' && (
          <div className="flex gap-2">
            <button
              onClick={() => onStatusUpdate(render.id, 'accepted')}
              className="flex-1 py-2 bg-green-500/10 border border-green-500/30 text-green-400 rounded-lg text-sm hover:bg-green-500/20 transition-colors flex items-center justify-center gap-1"
            >
              <Check className="w-4 h-4" />
              Approva
            </button>
            <button
              onClick={() => onStatusUpdate(render.id, 'rejected')}
              className="flex-1 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-colors flex items-center justify-center gap-1"
            >
              <X className="w-4 h-4" />
              Rifiuta
            </button>
          </div>
        )}
        
        {/* Proposal Link */}
        {render.proposal_id && (
          <Link
            to={`/proposals/${render.proposal_id}`}
            className="mt-3 w-full py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Vedi Proposta
            <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

// Comparison Modal Component
function ComparisonModal({ render, onClose }) {
  const [showBefore, setShowBefore] = useState(false);
  const metrics = render.quality_metrics || {};

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex flex-col"
      onClick={onClose}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <div>
          <h2 className="text-lg font-medium">{render.material_name || 'Render'}</h2>
          <p className="text-sm text-zinc-500">
            {new Date(render.created_at).toLocaleDateString('it-IT')}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-zinc-400 hover:text-white"
        >
          ✕
        </button>
      </div>
      
      {/* Image */}
      <div className="flex-1 flex items-center justify-center p-8" onClick={(e) => e.stopPropagation()}>
        <img
          src={showBefore ? render.source_image_url : render.result_image_url}
          alt={showBefore ? "Prima" : "Dopo"}
          className="max-w-full max-h-full object-contain"
        />
      </div>
      
      {/* Controls */}
      <div className="p-4 border-t border-zinc-800">
        <div className="max-w-3xl mx-auto">
          {/* Before/After Toggle */}
          <div className="flex justify-center gap-4 mb-4">
            <button
              onClick={(e) => { e.stopPropagation(); setShowBefore(false); }}
              className={`px-6 py-2 rounded-full transition-all ${
                !showBefore ? 'bg-[#B08B5C] text-black' : 'bg-zinc-800 text-white'
              }`}
            >
              Dopo
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowBefore(true); }}
              className={`px-6 py-2 rounded-full transition-all ${
                showBefore ? 'bg-[#B08B5C] text-black' : 'bg-zinc-800 text-white'
              }`}
            >
              Prima
            </button>
          </div>
          
          {/* Metrics */}
          {!showBefore && metrics.realism_score && (
            <div className="grid grid-cols-5 gap-4 text-center">
              <MetricDisplay label="Realismo" value={metrics.realism_score} />
              <MetricDisplay label="Luce" value={metrics.lighting_score} />
              <MetricDisplay label="Bordi" value={metrics.edge_quality_score} />
              <MetricDisplay label="Texture" value={metrics.texture_preservation_score} />
              <MetricDisplay label="Continuità" value={metrics.continuity_score} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricDisplay({ label, value }) {
  if (!value) return null;
  
  return (
    <div className="bg-zinc-800/50 rounded-lg p-3">
      <p className="text-xs text-zinc-500 mb-1">{label}</p>
      <p className="text-xl font-light text-[#C8A36A]">{value}%</p>
    </div>
  );
}
